import { updateWithToast } from '@/clientOnlyUtils/clientUtils';

import { DetailHeader } from '@poseidon/components/ContactDetail';
import { DetailHeaderOrg, DetailHeaderOrgLoading } from '@poseidon/components/DetailHeaderOrg';
import { SidebarNotes } from '@poseidon/components/SidebarNotes';
import {
  useMakeOrgFieldPrimaryMutation,
  useOrgSetFlagsMutation,
  useOrgUpdateMutation,
  useRemoveOrgFieldPrimaryMutation,
  useRemoveOrgFieldValueMutation,
} from '@/hooks/trpc/useOrgMutation';
import { useRouterQuery } from '@/hooks/useRouterQuery';
import { api } from '@/utils/api';
import { OrgDataSection } from '@poseidon/components/OrgDataSection';
import { OrgInteractSection } from '~/components/OrgInteractSection';
import { convertStringToOrgID, OrgSwitcher } from '~/components/OrgSwitcher';

type OrgDetailPage = {
  orgId?: string;
  authTeamId: string;
  orgFirst?: boolean;
  onClose: () => void;
};

export const OrgDetailPage = ({ orgId, authTeamId, orgFirst, onClose }: OrgDetailPage) => {
  const [query] = useRouterQuery();
  const orgDefaultId = convertStringToOrgID(query.detailType, 'interact');
  const detailQuery = api.org.detailPage.useQuery({ orgId: orgId || '' }, { enabled: Boolean(orgId) });

  const orgUpdate = useOrgUpdateMutation();
  const removeFieldValue = useRemoveOrgFieldValueMutation();
  const makeFieldPrimary = useMakeOrgFieldPrimaryMutation();
  const removeFieldPrimary = useRemoveOrgFieldPrimaryMutation();
  const setFlags = useOrgSetFlagsMutation();

  const orgData = detailQuery.data;

  if (detailQuery.isPending || orgData === undefined) {
    return (
      <DetailHeader footer={<OrgSwitcher defaultId="interact" />}>
        <DetailHeaderOrgLoading />
      </DetailHeader>
    );
  }

  if (orgData.org == null || orgData.orgId == null) {
    return <DetailHeader footer={<OrgSwitcher defaultId="interact" />}></DetailHeader>;
  }
  const onDebouncedNotesChange = (notes: string) =>
    updateWithToast({
      loading: 'Saving notes...',
      success: 'Notes saved',
      mutationFnAsync: () =>
        orgUpdate.mutateAsync({
          teamId: authTeamId,
          orgId: detailQuery.data.orgId,
          data: { source: 'crm', notes },
        }),
    });

  return (
    <>
      <DetailHeader onClose={onClose} footer={<OrgSwitcher defaultId="interact" />}>
        <DetailHeaderOrg
          name={detailQuery.data.orgPrimaryName ?? undefined}
          linkedinSlug={detailQuery.data.orgPrimaryFields.orgId?.data}
          pictureUrl={detailQuery.data.orgPictureUrl}
        />
      </DetailHeader>
      <div className="h-3" />
      {['interact', 'data'].includes(orgDefaultId) && (
        <SidebarNotes
          recordId={detailQuery.data.orgId}
          title="Notes"
          onDebounceChange={onDebouncedNotesChange}
          initialValue={detailQuery.data.org.notes ?? undefined}
        />
      )}
      {orgDefaultId === 'interact' && (
        <OrgInteractSection
          contacts={detailQuery.data.contacts}
          contactsLoading={detailQuery.isPending}
          dontClearOrg={orgFirst}
          stages={detailQuery.data.stages}
          stagesLoading={detailQuery.isPending}
        />
      )}
      {orgDefaultId === 'data' && (
        <OrgDataSection
          orgFields={detailQuery.data.org.fields}
          flags={detailQuery.data.flags}
          isFlagsPending={setFlags.isPending}
          onSetFlag={(flag, value) =>
            updateWithToast({
              loading: 'Updating...',
              success: 'Updated',
              mutationFnAsync: () =>
                setFlags.mutateAsync({
                  orgId: detailQuery.data.orgId,
                  ...(flag === 'isRsuGranting' ? { isRsuGranting: value } : { isEquityGranting: value }),
                }),
            })
          }
          onSetHqState={(value) =>
            updateWithToast({
              loading: 'Updating HQ state...',
              success: 'Updated',
              mutationFnAsync: () =>
                setFlags.mutateAsync({
                  orgId: detailQuery.data.orgId,
                  hqState: value,
                }),
            })
          }
          onAddValue={(name, value, source) =>
            updateWithToast({
              loading: 'Adding...',
              success: 'Added',
              mutationFnAsync: () =>
                orgUpdate.mutateAsync({
                  orgId: detailQuery.data.orgId,
                  teamId: authTeamId,
                  data: { [name]: value, source, primary: true },
                }),
            })
          }
          onDeleteValue={(field, source) =>
            updateWithToast({
              loading: 'Deleting...',
              success: 'Deleted',
              mutationFnAsync: () =>
                removeFieldValue.mutateAsync({ orgId: detailQuery.data.orgId, teamId: authTeamId, field, source }),
            })
          }
          onMakePrimary={(field, source) =>
            updateWithToast({
              loading: 'Updating...',
              success: 'Marked as primary',
              mutationFnAsync: () => makeFieldPrimary.mutateAsync({ orgId: detailQuery.data.orgId, data: { field, source } }),
            })
          }
          onRemovePrimary={(field, source) =>
            updateWithToast({
              loading: 'Updating...',
              success: 'Primary removed',
              mutationFnAsync: () =>
                removeFieldPrimary.mutateAsync({
                  orgId: detailQuery.data.orgId,
                  teamId: authTeamId,
                  data: { field, source },
                }),
            })
          }
        />
      )}
    </>
  );
};
